import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOut, faCog, faQuoteLeft, faCopyright } from '@fortawesome/pro-solid-svg-icons';
import { faHeadset, faMoneyBill } from '@fortawesome/pro-light-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { colorStack } from '../../../styleHelpers/colors';
import { fontSize } from '../../../styleHelpers/fontSizes';
import { IFullUserProfile } from '../../../entities/IProfile';
import { media } from '../../../styleHelpers/breakpoint';
import { SingleElement } from './Components/SingleElement';
import { IConfigReducer } from '../../../reducers/configReducer';
import { IState } from '../../../reducers';
import { ELegalClusterEnv } from '../../../entities/IConfiguration';
import { IBillingSubscription } from '../../../entities/Billing/Billing';
import { getBillingSubscriptions } from '../../../actions/billingActions';
import { modalsShadow } from '../../../styleHelpers/mixins/shadow';
import IntlMessage from '../../Common/IntlMessage';
import { IContextReducer } from '../../../reducers/contextReducer';
import { isTeamsInstance } from '../../../tools/runtimeInstance';
import {
    selectHeaderPaymentEnabled,
    selectHeaderPrivacyPolicyEnabled,
    selectHeaderProfileEnabled,
    selectHeaderSettingsEnabled,
    selectHeaderTermsEnabled,
    selectHelpdeskUrl,
    selectPlatformPolicyUrl
} from '../../../tools/platformConfiguration';

type GetBillingSubscriptions = ReturnType<typeof getBillingSubscriptions>;

const UserMenu = styled.div`
    ${media.desktop`
        position: absolute;
        top: 42px;
        right: 0;
        background: ${colorStack.white};
        ${modalsShadow()};
        overflow: hidden;
        border-radius: 8px;
    `}
`;

const Title = styled.div`
    font-size: ${fontSize[16]};
    color: ${colorStack.content};
    font-weight: 700;
    padding: 1rem 1rem .5rem 1rem;
`;

const LogoutButton = styled.button`
    cursor: pointer;
    outline: 0;
    margin-top: .5rem;
    padding: 1rem 0;
    font-weight: 500;
    border: 0;
    background: ${colorStack.whiteGrey};
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${colorStack.middleBlue};
    transition: color .2s ease;
    svg {
        margin-right: .5rem;
    }
    &:hover {
        color: ${colorStack.darkBlue};
    }
`;

interface IUserMenuDropdownProps {
    currentUserProfile: IFullUserProfile;
    closeDropdown?(): void;
}

export const UserMenuDropdown: FC<React.PropsWithChildren<IUserMenuDropdownProps>> = ({ currentUserProfile, closeDropdown }) => {
    const dispatch = useDispatch();
    const history = useNavigate();
    const { platformName, isLocalHost, headerProfileEnabled, headerSettingsEnabled, headerTermsEnabled, headerPrivacyPolicyEnabled, headerPaymentEnabled, helpdeskUrl, platformPolicyUrl } = useSelector<IState, IConfigReducer & IContextReducer & {
        headerProfileEnabled: boolean;
        headerSettingsEnabled: boolean;
        headerTermsEnabled: boolean;
        headerPrivacyPolicyEnabled: boolean;
        headerPaymentEnabled: boolean;
        helpdeskUrl: string;
        platformPolicyUrl: string;
    }>(state => ({
        ...state.config,
        ...state.context,
        headerProfileEnabled: selectHeaderProfileEnabled(state),
        headerSettingsEnabled: selectHeaderSettingsEnabled(state),
        headerTermsEnabled: selectHeaderTermsEnabled(state),
        headerPrivacyPolicyEnabled: selectHeaderPrivacyPolicyEnabled(state),
        headerPaymentEnabled: selectHeaderPaymentEnabled(state),
        helpdeskUrl: selectHelpdeskUrl(state),
        platformPolicyUrl: selectPlatformPolicyUrl(state)
    }));
    const [subscriptions, setSubscriptions] = useState<IBillingSubscription[]>(undefined);

    useEffect(() => {
        dispatch<GetBillingSubscriptions>(getBillingSubscriptions()).then((res) => setSubscriptions(res));
    }, []);

    const onLogout = useCallback(() => {
        if (isTeamsInstance()) {
            history('/teams/logout-start');
        } else {
            history('/logout');
        }
    }, []);

    return (
        <UserMenu>
            <Title>
                <IntlMessage id="topnav.account" />
            </Title>
            {headerProfileEnabled &&
                <SingleElement
                    textNode={<IntlMessage id="topnav.viewMyProfile" />}
                    link={`/profile/${currentUserProfile?.id}`}
                    imagePath={currentUserProfile?.picture}
                    actionHandler={closeDropdown}
                />
            }
            {headerPaymentEnabled && (isLocalHost || platformName === ELegalClusterEnv.lcDev || platformName === ELegalClusterEnv.lcRec || platformName === ELegalClusterEnv.lcDemo || platformName === ELegalClusterEnv.lcProd) && subscriptions?.length > 0 &&
                <SingleElement
                    textNode={<IntlMessage id="topnav.billing" />}
                    link={'/billing'}
                    icon={faMoneyBill}
                    actionHandler={closeDropdown}
                />
            }
            {headerSettingsEnabled &&
                <SingleElement
                    textNode={<IntlMessage id="topnav.settings" />}
                    link="/settings"
                    icon={faCog}
                    actionHandler={closeDropdown}
                />
            }
            {headerTermsEnabled &&
                <SingleElement
                    textNode={<IntlMessage id="topnav.information.terms" />}
                    link="/terms"
                    icon={faQuoteLeft}
                    actionHandler={closeDropdown}
                />
            }
            {headerPrivacyPolicyEnabled &&
                <SingleElement
                    textNode={<IntlMessage id="topnav.information.privacy" />}
                    link={platformPolicyUrl?.startsWith('http') ? undefined : (platformPolicyUrl || '/copyrights')}
                    icon={faCopyright}
                    actionHandler={() => {
                        closeDropdown?.();
                        if (platformPolicyUrl?.startsWith('http')) {
                            window.open(platformPolicyUrl, '_blank', 'noopener,noreferrer');
                        }
                    }}
                />
            }
            {helpdeskUrl &&
                <SingleElement
                    textNode={<IntlMessage id="admin.configuration.basic.helpdeskUrl" defaultMessage="Helpdesk" />}
                    icon={faHeadset}
                    actionHandler={() => {
                        closeDropdown?.();
                        window.open(helpdeskUrl, '_blank', 'noopener,noreferrer');
                    }}
                />
            }
            <LogoutButton onClick={onLogout}>
                <FontAwesomeIcon icon={faSignOut} size="lg" />
                <IntlMessage id="topnav.logout" />
            </LogoutButton>
        </UserMenu>
    );
};
